import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export const useAdmin = (key, url) =>
  useQuery({ queryKey: ["admin", key], queryFn: async () => (await api.get(url)).data, staleTime: 15_000 });

export const PageHead = ({ eyebrow, title, children }) => (
  <div className="ad-head">
    <div><p className="eyebrow">{eyebrow}</p><h1>{title}</h1></div>
    {children && <div className="ad-head-actions">{children}</div>}
  </div>
);

export const Stat = ({ label, value, hint, testId }) => (
  <div className="ad-stat" data-testid={testId}>
    <span>{label}</span><strong>{value}</strong>{hint && <small>{hint}</small>}
  </div>
);

export const Loading = () => <p className="muted-sm ad-loading" data-testid="admin-loading"><Loader2 size={16} className="spin" /> Loading…</p>;
export const Empty = ({ children }) => <p className="empty" data-testid="admin-empty">{children}</p>;

export const fmtWhen = (d) => (d ? new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
export const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export const Chips = ({ options, value, onChange, testPrefix }) => (
  <div className="ad-chips">
    {options.map(([k, label]) => (
      <button key={k} type="button" className="ad-chip" aria-pressed={value === k} onClick={() => onChange(k)} data-testid={`${testPrefix}-${k}`}>{label}</button>
    ))}
  </div>
);

export const downloadCsv = (name, rows) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const head = Object.keys(rows[0] || {});
  const text = [head.map(esc).join(","), ...rows.map((r) => head.map((h) => esc(r[h])).join(","))].join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([text], { type: "text/csv" }));
  a.download = name;
  a.click();
};

export const ImageField = ({ value, onChange, testId }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      onChange(data.url);
    } catch (er) {
      setErr(er?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  const preview = !value ? "" : value.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${value}` : value.startsWith("http") ? value : `https://images.unsplash.com/photo-${value}?auto=format&fit=crop&w=300&q=60`;
  return (
    <div className="ad-image" data-testid={testId}>
      {preview && (/\.(mp4|webm|mov)$/i.test(value) ? <video src={preview} muted playsInline loop autoPlay /> : <img src={preview} alt="" />)}
      <div className="ad-image-ctrl">
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Image/video URL or upload" data-testid={`${testId}-input`} />
        <label className="btn light ad-upload">
          {busy ? <Loader2 size={14} className="spin" /> : "Upload"}
          <input type="file" accept="image/*,video/mp4,video/webm,video/quicktime" hidden onChange={upload} data-testid={`${testId}-file`} />
        </label>
      </div>
      {err && <p className="form-error">{err}</p>}
    </div>
  );
};
